import { Bell, Bookmark, CreditCard, FileText, Folder, Home, LayoutDashboard, Menu, MessageSquareText, MonitorPlay, PlusSquare, Search, Settings, ShoppingCart, Sparkles, Users, WalletCards, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { fetchSiteSettings } from "@/api/settings";
import { useAuthStore } from "@/store/auth-store";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/cn";
import { isPngImage } from "@/lib/image-format";

interface NavItem {
  label: string;
  href: string;
  icon: typeof Home;
}

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    siteSettings,
    searchQuery,
    setSearchQuery,
    updateSiteSettings,
    logout,
    isAdmin,
    isPlatformAdmin,
    isTenantAdmin,
    isCustomerView,
    setCustomerView,
    isCentralDomain,
    resolveAppPath,
  } = useAuthStore();
  const central = isCentralDomain();
  const admin = isAdmin();
  const platformAdmin = isPlatformAdmin();
  const tenantAdmin = isTenantAdmin();
  const customerPreview = isCustomerView();
  const communityEnabled = Boolean(siteSettings?.hasForum);
  const hasTransparentLogo = isPngImage(siteSettings?.siteLogo);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    if (central) {
      return;
    }

    let active = true;
    void fetchSiteSettings()
      .then((settings) => {
        if (active) {
          updateSiteSettings(settings);
        }
      })
      .catch(() => { });

    return () => {
      active = false;
    };
  }, [central, updateSiteSettings]);
  const modeLabel = central ? "Central domain" : admin ? "Creator view" : "User view";
  const navItems: NavItem[] = central
    ? platformAdmin
      ? [
        { label: "Vaults", href: resolveAppPath("/tenant"), icon: MonitorPlay },
        { label: "Payouts", href: resolveAppPath("/payouts"), icon: WalletCards },
      ]
      : [
        { label: "Create Vault", href: "/create-vault", icon: Sparkles },
      ]
    : admin
      ? [
        { label: "Create Post", href: resolveAppPath("/create/text"), icon: PlusSquare },
        { label: "Posts", href: resolveAppPath("/posts"), icon: FileText },
        { label: "Folders", href: resolveAppPath("/folders"), icon: Folder },
        { label: "Dashboard", href: resolveAppPath("/dashboard"), icon: LayoutDashboard },
        { label: "Members", href: resolveAppPath("/members"), icon: Users },
        { label: "Marketing", href: resolveAppPath("/marketing"), icon: Sparkles },
        ...(communityEnabled ? [{ label: "Community", href: resolveAppPath("/community"), icon: MessageSquareText }] : []),
        { label: "Subscriptions", href: resolveAppPath("/memberships"), icon: CreditCard },
        { label: "Orders", href: resolveAppPath("/orders"), icon: ShoppingCart },
        { label: "Payouts", href: resolveAppPath("/payouts"), icon: WalletCards },
        // { label: "Events", href: resolveAppPath("/events"), icon: MonitorPlay },
        { label: "My Page", href: resolveAppPath("/home"), icon: Home },
        { label: "Edit Page", href: resolveAppPath("/settings"), icon: Settings },
      ]
      : [
        { label: "Home", href: resolveAppPath("/home"), icon: Home },
        ...(communityEnabled ? [{ label: "Community", href: resolveAppPath("/community"), icon: MessageSquareText }] : []),
        { label: "Subscription", href: resolveAppPath("/subscription"), icon: CreditCard },
        // { label: "Events", href: resolveAppPath("/events"), icon: MonitorPlay },
      ];

  const pageTitle = siteSettings?.siteName || "Fan Vault";
  const showSearch = location.pathname.endsWith("/home") || location.pathname === "/home";
  const hideLogoutAction = central && location.pathname === "/create-vault";

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  async function handleLogout() {
    await logout();
    setMobileNavOpen(false);
    navigate(resolveAppPath("/login"), { replace: true });
  }

  function renderNavContent() {
    return (
      <>
        <div className="mb-6 flex justify-between gap-3 px-2 pt-2">
          <div className="flex items-center gap-3">
            <div className={cn(
              "flex h-14 w-14 items-center justify-center rounded-2xl border p-2 shadow-[0_10px_30px_rgba(15,23,42,0.35)]",
              hasTransparentLogo ? "border-white/10 bg-transparent" : "border-white/10 bg-white/95",
            )}>
              <Avatar alt={pageTitle} src={siteSettings?.siteLogo} className="h-full w-full rounded-xl border-0 bg-transparent object-contain" />
            </div>

            <div>
              <p className="text-lg font-semibold text-white">{pageTitle}</p>
              {!central && tenantAdmin ? <p className="text-xs uppercase tracking-[0.22em] text-slate-400">{modeLabel}</p> : null}
            </div>
          </div>
          <Button className="cursor-pointer lg:hidden" onClick={() => setMobileNavOpen((open) => !open)}>X</Button>

        </div>
        <nav className="space-y-1">
          {navItems.map((item) => {
            const active = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
            const Icon = item.icon;
            return (
              <Link
                className={cn(
                  "flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold transition",
                  active ? "bg-blurple-500/16 text-white" : "text-slate-300 hover:bg-white/6 hover:text-white",
                )}
                key={item.href}
                to={item.href}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        {!hideLogoutAction ? (
          <Button className="mt-6 w-full" onClick={handleLogout} variant="outline">
            Log out
          </Button>
        ) : null}
      </>
    );
  }

  return (
    <div className="page-frame min-h-screen px-3 py-3 md:px-5 md:py-5">
      <div className="mx-auto grid min-h-[calc(100vh-24px)] max-w-[1600px] grid-cols-1 gap-4 lg:grid-cols-[290px_minmax(0,1fr)]">
        <Card className="hidden p-4 lg:block">{renderNavContent()}</Card>
        <div className="flex min-w-0 flex-col gap-4">
          <Card className="p-3 md:p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 lg:hidden">
                <Button
                  aria-expanded={mobileNavOpen}
                  aria-label={mobileNavOpen ? "Close navigation menu" : "Open navigation menu"}
                  onClick={() => setMobileNavOpen((open) => !open)}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  {mobileNavOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                </Button>
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-2xl border p-1.5 shadow-[0_10px_30px_rgba(15,23,42,0.35)]",
                    hasTransparentLogo ? "border-white/10 bg-transparent" : "border-white/10 bg-white/95",
                  )}>
                    <Avatar alt={pageTitle} src={siteSettings?.siteLogo} className="h-full w-full rounded-xl border-0 bg-transparent object-contain" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-white">{pageTitle}</p>
                    {!central && tenantAdmin ? <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400">{modeLabel}</p> : null}
                  </div>
                </div>
              </div>
              {!central && tenantAdmin ? (
                <div className="group relative">
                  <button
                    aria-checked={customerPreview}
                    aria-label="Toggle creator and user view"
                    className="cursor-pointer flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-200 transition hover:bg-white/10"
                    onClick={() => {
                      const next = !customerPreview;
                      setCustomerView(next);
                      navigate(resolveAppPath("/home"), { replace: true });
                    }}
                    role="switch"
                    type="button"
                  >
                    <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">
                      {customerPreview ? "User" : "Creator"}
                    </span>
                    <span className={cn(
                      "relative flex h-7 w-12 items-center rounded-full transition",
                      customerPreview ? "bg-blurple-500/80" : "bg-slate-600/80",
                    )}>
                      <span
                        className={cn(
                          "absolute h-5 w-5 rounded-full bg-white shadow-[0_3px_10px_rgba(15,23,42,0.35)] transition-transform",
                          customerPreview ? "translate-x-6" : "translate-x-1",
                        )}
                      />
                    </span>
                  </button>
                  <div className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden w-64 -translate-x-1/2 rounded-2xl border border-white/10 bg-slate-950/95 px-4 py-3 text-xs leading-6 text-slate-200 shadow-[0_16px_50px_rgba(15,23,42,0.45)] group-hover:block group-focus-within:block">
                    This switch lets you preview the page the way your users and subscribers see it.
                  </div>
                </div>
              ) : null}
              {!central ? (
                <div className="flex items-center gap-1 rounded-2xl border border-white/10 bg-white/5 p-1">
                  <Link className="rounded-[18px] p-3 text-slate-200 transition hover:bg-white/10" to={resolveAppPath("/bookmarks")}>
                    <Bookmark className="h-4 w-4" />
                  </Link>
                  <Link className="rounded-[18px] p-3 text-slate-200 transition hover:bg-white/10" to={resolveAppPath("/notifications")}>
                    <Bell className="h-4 w-4" />
                  </Link>
                </div>
              ) : null}
              {showSearch && !central ? (
                <label className="flex min-w-[220px] items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-300">
                  <Search className="h-4 w-4" />
                  <input
                    className="w-full bg-transparent outline-none placeholder:text-slate-500"
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search posts"
                    value={searchQuery}
                  />
                </label>
              ) : null}
              <Link className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-200" to={resolveAppPath("/profile")}>
                <Avatar src={user?.avatarUrl} />
                <span>{user?.name || "Profile"}</span>
              </Link>
            </div>
          </Card>
          {mobileNavOpen ? (
            <div className="lg:hidden">
              <div className="fixed inset-0 z-40 bg-slate-950/70 backdrop-blur-sm" onClick={() => setMobileNavOpen(false)} />
              <Card className="fixed inset-x-3 top-20 z-50 max-h-[calc(100vh-6rem)] overflow-y-auto p-4 md:inset-x-5">
                {renderNavContent()}
              </Card>
            </div>
          ) : null}
          <div className="min-w-0">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
